import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import type { Price, ProductWithPrice } from 'types';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import {
  formatMoney,
  monthlyCents,
  toTeamPlans,
} from '@/components/b2b/teamPlans';
import { usePlanCheckout } from '@/components/pricing/usePlanCheckout';

import { useUser } from '@/utils/useUser';

import { useEmployeesT, useHireHref } from './kit';

type BillingInterval = 'month' | 'year';

/** One plan card, with the limits that matter for AI employees. */
export type EmployeePlan = {
  name: 'Free' | 'Starter' | 'Unlimited' | 'Teams';
  employees: number;
  words: number;
  /** Stripe product with its active prices (paid plans except Teams). */
  product?: ProductWithPrice;
};

const priceFor = (
  product: ProductWithPrice | undefined,
  wanted: BillingInterval,
) =>
  product?.prices?.find((price) => price.interval === wanted) as
    | Price
    | undefined;

export default function EmployeesPricing({
  plans,
  teamProducts,
}: {
  plans: EmployeePlan[];
  teamProducts: ProductWithPrice[];
}) {
  const { t } = useEmployeesT();
  const router = useRouter();
  // Arabic defaults to Arabic-Indic digits; the page copy uses Latin ones.
  const locale = router.locale === 'ar' ? 'ar-u-nu-latn' : router.locale;
  const [interval, setBillingInterval] = useState<BillingInterval>('year');
  const { subscription } = useUser();
  const hireHref = useHireHref();
  const { handleCheckout, priceIdLoading, planChangeModal } = usePlanCheckout({
    isUpgrade: true,
  });

  const plain = (value: number) => new Intl.NumberFormat(locale).format(value);

  const bestSaving = useMemo(() => {
    let best = 0;
    for (const plan of plans) {
      const monthly = priceFor(plan.product, 'month')?.unit_amount;
      const yearly = priceFor(plan.product, 'year')?.unit_amount;
      if (monthly && yearly) best = Math.max(best, 1 - yearly / (monthly * 12));
    }
    return Math.round(best * 100);
  }, [plans]);

  // The smallest Teams plan (3 seats): the price a team starts at.
  const teamPlan = useMemo(() => toTeamPlans(teamProducts)[0], [teamProducts]);
  const currency =
    plans.find((plan) => plan.product)?.product?.prices?.[0]?.currency ?? 'usd';
  const money = (cents: number) => formatMoney(cents, currency, locale);
  const currentName = subscription?.prices?.products?.name;

  const featuresFor = (plan: EmployeePlan): string[] => {
    const employees =
      plan.employees === 1
        ? t('1 AI employee')
        : plan.name === 'Teams'
          ? t('{{count}} AI employees for each person', {
              count: plain(plan.employees),
            })
          : t('{{count}} AI employees', { count: plain(plan.employees) });
    const words =
      plan.name === 'Teams'
        ? t('{{count}} words a month for each person', {
            count: plain(plan.words),
          })
        : t('{{count}} words a month', { count: plain(plan.words) });
    const extras: Record<EmployeePlan['name'], string[]> = {
      Free: [t('Approvals, tasks and routines'), t('Our fast AI models')],
      Starter: [
        t('Group chats for your team'),
        t('Top models from OpenAI, Anthropic and Google'),
      ],
      Unlimited: [
        t('Group chats for your team'),
        t('Top models from OpenAI, Anthropic and Google'),
      ],
      Teams: [t('Seats for your whole team'), t('One bill for everyone')],
    };
    return [employees, words, ...extras[plan.name]];
  };

  return (
    <section id='pricing' className='scroll-mt-8 py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
          <SectionHeading
            title={t('Start free.')}
            tail={t('Grow the team when you’re ready.')}
            lead={t(
              'Every plan comes with every role, approvals, tasks and routines. Bigger plans let you hire more people.',
            )}
          />
          <div
            role='radiogroup'
            aria-label={t('Billing period')}
            className={clsxm(
              'inline-flex flex-none self-start rounded-full bg-white p-1 lg:self-auto dark:bg-[#14141f]',
              tone.line,
            )}
          >
            {(['month', 'year'] as const).map((value) => {
              const active = interval === value;
              return (
                <button
                  key={value}
                  type='button'
                  role='radio'
                  aria-checked={active}
                  onClick={() => setBillingInterval(value)}
                  className={clsxm(
                    'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                    active
                      ? 'bg-[#10101c] text-white dark:bg-white dark:text-[#10101c]'
                      : clsxm(
                          tone.body,
                          'hover:text-[#10101c] dark:hover:text-white',
                        ),
                  )}
                >
                  {value === 'month' ? t('Monthly') : t('Yearly')}
                  {value === 'year' && bestSaving > 0 ? (
                    <span
                      className={clsxm(
                        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                        active
                          ? 'bg-emerald-400 text-[#06281c]'
                          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-300',
                      )}
                    >
                      {t('Save up to {{percent}}%', { percent: bestSaving })}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <ul className='mt-14 grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
          {plans.map((plan) => {
            const featured = plan.name === 'Unlimited';
            const price =
              priceFor(plan.product, interval) ??
              priceFor(plan.product, 'month');
            const isCurrent = Boolean(currentName && currentName === plan.name);
            let amount: string | null = null;
            let note: string | null = null;
            if (plan.name === 'Free') {
              amount = money(0);
              note = t('Free forever');
            } else if (plan.name === 'Teams') {
              const teamPrice =
                teamPlan?.prices[interval] ?? teamPlan?.prices.month;
              if (teamPlan && teamPrice) {
                amount = money(monthlyCents(teamPrice) / teamPlan.seats);
                note =
                  teamPrice.interval === 'year'
                    ? t('per person a month, billed yearly')
                    : t('per person a month');
              }
            } else if (price) {
              amount = money(monthlyCents(price));
              note =
                price.interval === 'year'
                  ? t('a month, billed yearly')
                  : t('a month');
            }

            return (
              <li
                key={plan.name}
                className={clsxm(
                  'relative flex flex-col rounded-[28px] p-6 sm:p-7',
                  featured
                    ? 'bg-[#0d0c1f] text-white shadow-[0_40px_80px_-40px_rgba(22,12,90,0.8)] ring-1 ring-white/10'
                    : clsxm('bg-white dark:bg-[#14141f]', tone.line),
                )}
              >
                <div className='flex items-center justify-between gap-3'>
                  <p
                    className={clsxm(
                      'text-[15px] font-semibold',
                      featured ? 'text-white' : tone.ink,
                    )}
                  >
                    {t(plan.name)}
                  </p>
                  {featured ? (
                    <span className='rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-semibold text-white'>
                      {t('Recommended')}
                    </span>
                  ) : null}
                </div>
                <p className='mt-5 flex items-baseline gap-2'>
                  <span
                    className={clsxm(
                      'text-[2.75rem] font-semibold leading-none tracking-[-0.04em] tabular-nums',
                      featured ? 'text-white' : tone.ink,
                    )}
                  >
                    {amount ?? '–'}
                  </span>
                </p>
                <p
                  className={clsxm(
                    'mt-2 min-h-[1.25rem] text-[13px]',
                    featured ? 'text-white/60' : tone.quiet,
                  )}
                >
                  {note}
                </p>

                <ul className='mt-7 flex-1 space-y-3'>
                  {featuresFor(plan).map((item, index) => (
                    <li
                      key={item}
                      className={clsxm(
                        'flex gap-3 text-[14px] leading-6',
                        featured ? 'text-white/85' : tone.body,
                        index === 0 && 'font-semibold',
                        index === 0 && (featured ? 'text-white' : tone.ink),
                      )}
                    >
                      <CheckGlyph
                        className={clsxm(
                          'mt-1',
                          featured
                            ? 'text-primary-300'
                            : 'text-primary-600 dark:text-primary-400',
                        )}
                      />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className='pt-8'>
                  {plan.name === 'Free' ? (
                    <Link
                      href={hireHref}
                      className={clsxm(buttonSecondary, 'w-full')}
                    >
                      {t('Hire your first employee')}
                    </Link>
                  ) : plan.name === 'Teams' ? (
                    <Link
                      href='/b2b#pricing'
                      className={clsxm(buttonSecondary, 'w-full')}
                    >
                      {t('See team plans')}
                    </Link>
                  ) : price && plan.product ? (
                    <button
                      type='button'
                      onClick={() =>
                        plan.product && handleCheckout(price, plan.product)
                      }
                      disabled={priceIdLoading === price.id}
                      className={clsxm(
                        featured
                          ? clsxm(buttonPrimary, 'w-full')
                          : clsxm(buttonSecondary, 'w-full'),
                        'disabled:opacity-70',
                      )}
                    >
                      {priceIdLoading === price.id ? (
                        <span className='h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current' />
                      ) : null}
                      {isCurrent
                        ? t('Manage your plan')
                        : t('Get {{plan}}', { plan: t(plan.name) })}
                      <ArrowGlyph />
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>

        <p
          className={clsxm(
            'mx-auto mt-8 max-w-2xl text-center text-sm leading-6',
            tone.quiet,
          )}
        >
          {t(
            'Employees use your plan’s monthly words, the same allowance as Marky Agent. Faster models use fewer words.',
          )}{' '}
          <Link
            href='/pricing'
            className='font-medium text-primary-700 underline-offset-4 hover:underline dark:text-primary-300'
          >
            {t('Compare all plans')}
          </Link>
        </p>
      </div>
      {planChangeModal}
    </section>
  );
}
